import { forwardRef } from 'react';
import { clsx } from 'clsx';
import { ChevronDown } from 'lucide-react';

const Select = forwardRef(({
  label,
  helperText,
  error,
  id,
  className,
  required,
  disabled,
  children,
  ...props
}, ref) => {
  const selectId = id || label?.toLowerCase().replace(/\s+/g, '-');

  return (
    <div className="flex flex-col gap-1.5 w-full">
      {label && (
        <div className="flex items-center gap-0.5">
          <label
            htmlFor={selectId}
            className={clsx(
              'text-sm font-medium',
              disabled ? 'text-text-disabled' : 'text-text-secondary',
            )}
          >
            {label}
          </label>
          {required && <span className="text-danger text-sm leading-none" aria-hidden="true">*</span>}
        </div>
      )}

      <div className="relative">
        <select
          ref={ref}
          id={selectId}
          disabled={disabled}
          required={required}
          aria-invalid={!!error}
          aria-describedby={error ? `${selectId}-error` : helperText ? `${selectId}-helper` : undefined}
          className={clsx(
            'w-full appearance-none rounded-md border px-3.5 py-2.5 pr-10 text-base bg-surface text-text-primary',
            'transition-colors duration-normal',
            'focus:outline-none focus:ring-2 focus:ring-border-focus focus:border-border-focus',
            'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-bg-secondary',
            error
              ? 'border-danger focus:ring-danger'
              : 'border-border hover:border-border-strong',
            className,
          )}
          {...props}
        >
          {children}
        </select>
        <ChevronDown
          size={16}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
          aria-hidden="true"
        />
      </div>

      {error && (
        <p id={`${selectId}-error`} role="alert" className="text-xs text-danger-text flex items-center gap-1">
          {error}
        </p>
      )}
      {!error && helperText && (
        <p id={`${selectId}-helper`} className="text-xs text-text-muted">
          {helperText}
        </p>
      )}
    </div>
  );
});

Select.displayName = 'Select';

export default Select;
